import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ContentSummary } from '@cms/contracts'
import { listContents } from '../api/endpoints.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { Card, StatusChip } from '../ui/primitives.js'
import { contentStatusTone } from '../ui/status.js'

/**
 * The same content in the other languages: the members of the translation
 * group (`translationGroup`), each one a jump to its own editor. The group is
 * decided when the content is imported or translated; from here it is read.
 */
export function ContentTranslationsCard({ siteId, contentId, group }: { siteId: string; contentId: string; group: string | null | undefined }) {
  const { t } = useI18n()
  const [members, setMembers] = useState<ContentSummary[] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!group) { setMembers([]); return }
    let active = true
    setFailed(false)
    listContents({ siteId, limit: 100 })
      .then((page) => { if (active) setMembers(page.items.filter((entry) => entry.translationGroup === group && entry.id !== contentId && entry.status !== 'trashed')) })
      .catch(() => { if (active) { setFailed(true); setMembers([]) } })
    return () => { active = false }
  }, [siteId, contentId, group])

  return <Card id="sezione-traduzioni" title={t('editor.translations')} subtitle={t('editor.translationsHelp')} icon="translate">
    {failed && <p className="muted">{t('editor.translationsLoadError')}</p>}
    {!failed && members !== null && members.length === 0 && <p className="muted">{t('editor.noTranslations')}</p>}
    {members && members.length > 0 && <ul className="translation-list">
      {members.map((member) => <li key={member.id}>
        <StatusChip tone="neutral">{member.locale.toUpperCase()}</StatusChip>
        <span className="translation-title">{member.title || member.slug}</span>
        <StatusChip tone={contentStatusTone(member.status)}>{t(`status.${member.status}`)}</StatusChip>
        <Link className="translation-open" to={paths.content(siteId, member.id)}>{t('editor.openTranslation')}</Link>
      </li>)}
    </ul>}
  </Card>
}
